import type { ServerSideSelectionState } from '@civilgrid/ui/components';
import { getNonNullUserName } from '@civilgrid/ui/utils';
import { useCallback, useMemo, useState } from 'react';
import type { ProjectsSearchPayload } from '~/api/projectsV3';
import type { Project } from '~/common/types';
import { useBulkUpdateProjects } from '../../../queries';
import { setTableSelection } from '../../../store';

export type ProjectManagerOption = { value: string; label: string };

export type UseBulkReassignParams = {
  organizationId: string | null;
  buildSearchPayloadForBulk: () => ProjectsSearchPayload;
  orgMembers: Array<{
    user?: {
      id: string;
      status?: string;
      firstName?: string;
      lastName?: string;
      name?: string;
      email?: string;
    };
  }>;
  /** When provided and exactly one project is selected, the combobox is pre-filled with that project's manager */
  getProjectById?: (id: string) => Project | undefined;
};

export function useBulkReassign({
  organizationId,
  buildSearchPayloadForBulk,
  orgMembers,
  getProjectById,
}: UseBulkReassignParams) {
  const [pendingSelection, setPendingSelection] = useState<ServerSideSelectionState | null>(null);
  const [projectManagerId, setProjectManagerId] = useState<string>('');
  const mutation = useBulkUpdateProjects();

  const projectManagerOptions: ProjectManagerOption[] = useMemo(
    () =>
      (orgMembers ?? [])
        .filter((m) => m.user?.status === 'active')
        .map((m) => ({
          value: m.user!.id,
          label:
            [m.user!.firstName, m.user!.lastName].filter(Boolean).join(' ') ||
            getNonNullUserName(m.user!.name) ||
            m.user!.email ||
            '',
        })),
    [orgMembers],
  );

  const open = useCallback(
    (selection: ServerSideSelectionState) => {
      setPendingSelection(selection);
      const isSingleProject = selection.mode === 'partial' && selection.selectedIds.length === 1 && getProjectById;
      if (isSingleProject) {
        const project = getProjectById(selection.selectedIds[0]);
        if (project?.projectManagerId) {
          setProjectManagerId(project.projectManagerId);
          return;
        }
      }
      setProjectManagerId('');
    },
    [getProjectById],
  );

  const close = useCallback(() => {
    setPendingSelection(null);
  }, []);

  const onSubmit = useCallback(() => {
    if (!organizationId || !pendingSelection || !projectManagerId) return;
    mutation.mutate(
      {
        organizationId,
        selection:
          pendingSelection.mode === 'all'
            ? { ...pendingSelection, search: buildSearchPayloadForBulk() }
            : pendingSelection,
        updates: { projectManagerId },
      },
      {
        onSuccess: () => {
          setPendingSelection(null);
          setTableSelection({ mode: 'partial', selectedIds: [] });
        },
      },
    );
  }, [organizationId, pendingSelection, projectManagerId, buildSearchPayloadForBulk, mutation]);

  return {
    isOpen: pendingSelection !== null,
    open,
    close,
    projectManagerId,
    setProjectManagerId,
    projectManagerOptions,
    onSubmit,
    isPending: mutation.isPending,
  };
}
